import React, { useEffect, useState, useCallback } from "react";
import { useParams, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight, ExternalLink, Calendar, Info, Lock } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import axios from "axios";
import PendingOrdersList from "@/pages/PendingOrdersList";
import DescriptionCell from "@/pages/DescriptionCell";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SharedDashboard = () => {
  const { shareId, memberId, month } = useParams();
  const location = useLocation();
  const isPendingRoute = location.pathname.endsWith('/pending');
  const [data, setData] = useState(null);
  const [pendingData, setPendingData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [period, setPeriod] = useState(() => {
    if (isPendingRoute) return "pending";
    if (month) {
      if (month.startsWith('week-')) return "weekly";
      return "monthly";
    }
    return "weekly";
  });
  const [refDate, setRefDate] = useState(() => {
    if (month) {
      // Week param: "week-2026-03-16"
      const weekMatch = month.match(/^week-(\d{4}-\d{2}-\d{2})$/);
      if (weekMatch) return weekMatch[1];
      // Month ISO: "2026-03"
      const isoMatch = month.match(/^(\d{4})-(\d{2})$/);
      if (isoMatch) return `${isoMatch[1]}-${isoMatch[2]}-01`;
      // Month named: "march-2026"
      const monthNames = { january: '01', february: '02', march: '03', april: '04', may: '05', june: '06', july: '07', august: '08', september: '09', october: '10', november: '11', december: '12' };
      const namedMatch = month.match(/^([a-z]+)-(\d{4})$/i);
      if (namedMatch && monthNames[namedMatch[1].toLowerCase()]) {
        return `${namedMatch[2]}-${monthNames[namedMatch[1].toLowerCase()]}-01`;
      }
    }
    const now = new Date();
    const day = now.getDay();
    const diff = day === 0 ? 6 : day - 1;
    const monday = new Date(now);
    monday.setDate(now.getDate() - diff);
    return monday.toISOString().split('T')[0];
  });

  const loadOrders = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      if (period === 'pending') {
        const url = memberId
          ? `${BACKEND_URL}/api/shared/${shareId}/member/${memberId}/pending`
          : `${BACKEND_URL}/api/shared/${shareId}/pending`;
        const res = await axios.get(url, { params: { months: 12 } });
        setPendingData(res.data);
      } else {
        const params = { period, ref_date: refDate };
        if (memberId) params.member_id = memberId;
        const response = await axios.get(`${BACKEND_URL}/api/shared/${shareId}`, { params });
        setData(response.data);
      }
    } catch (err) {
      setError(err.response?.status === 404 ? 'This link is no longer valid' : 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, [shareId, period, refDate, memberId]);

  useEffect(() => { loadOrders(); }, [loadOrders]);

  const navigateDate = (direction) => {
    const d = new Date(refDate);
    if (period === "weekly") d.setDate(d.getDate() + (direction === 'next' ? 7 : -7));
    else d.setMonth(d.getMonth() + (direction === 'next' ? 1 : -1));
    setRefDate(d.toISOString().split('T')[0]);
  };

  const handlePeriodChange = (newPeriod) => {
    setPeriod(newPeriod);
    if (newPeriod === 'pending') return;
    const now = new Date();
    if (newPeriod === "weekly") {
      const day = now.getDay();
      const diff = day === 0 ? 6 : day - 1;
      now.setDate(now.getDate() - diff);
    } else {
      now.setDate(1);
    }
    setRefDate(now.toISOString().split('T')[0]);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
  };

  const shortenDesc = (desc, maxLen = 32) => {
    if (!desc) return '';
    if (desc.length <= maxLen) return desc;
    return desc.substring(0, maxLen) + '...';
  };

  const dateRangeLabel = () => {
    if (!data) return '';
    const s = new Date(data.start_date + 'T00:00:00');
    const e = new Date(data.end_date + 'T00:00:00');
    e.setDate(e.getDate() - 1);
    if (period === 'weekly') {
      return `${s.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })} - ${e.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}`;
    }
    return s.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
  };

  const fmt = (n) => {
    if (!n && n !== 0) return '-';
    return n.toLocaleString('en-IN');
  };

  if (error) {
    return (
      <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center px-4">
        <div className="text-center">
          <Calendar className="w-10 h-10 text-stone-300 mx-auto mb-3" />
          <p className="text-stone-600 font-heading font-bold text-sm">{error}</p>
        </div>
      </div>
    );
  }

  const viewer = data?.viewer || pendingData?.viewer;
  const isMemberView = !!memberId && !!viewer;
  const isPendingView = period === 'pending';

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      {/* Header */}
      <header className="bg-white border-b border-stone-100">
        <div className="max-w-3xl mx-auto px-3 sm:px-6 py-3 sm:py-4">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-lg sm:rounded-xl flex-shrink-0 overflow-hidden">
                  <img src="/logo.png" alt="Tell M" className="w-full h-full object-cover" />
                </div>
                <h1 className="text-base sm:text-lg font-heading font-extrabold text-stone-900 truncate">{data?.household_name || pendingData?.household_name || 'Loading...'}</h1>
              </div>
              <div className="flex items-center gap-1.5 mt-1 ml-10 sm:ml-[44px] flex-wrap">
                <button onClick={() => navigateDate('prev')} className={`text-stone-400 hover:text-stone-600 transition p-0.5 ${isPendingView ? 'invisible' : ''}`}>
                  <ChevronLeft className="w-3.5 h-3.5" />
                </button>
                <span className="text-xs sm:text-sm text-stone-500">{isPendingView ? 'All pending bills' : dateRangeLabel()}</span>
                <button onClick={() => navigateDate('next')} className={`text-stone-400 hover:text-stone-600 transition p-0.5 ${isPendingView ? 'invisible' : ''}`}>
                  <ChevronRight className="w-3.5 h-3.5" />
                </button>
                {isMemberView && (
                  <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-semibold ml-1"
                        style={{ background: `${viewer.color}22`, color: viewer.color }}
                        data-testid="viewer-badge">
                    <span className="w-3.5 h-3.5 rounded-full flex items-center justify-center text-white text-[8px] font-bold" style={{ background: viewer.color }}>{viewer.name[0]?.toUpperCase()}</span>
                    {viewer.name}'s view
                  </span>
                )}
                {period === 'monthly' && data?.is_month_closed && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-50 border border-amber-200 text-amber-700 rounded-full text-[10px] font-bold uppercase tracking-wider" data-testid="shared-finalized-badge">
                    <Lock className="w-3 h-3" /> Finalized
                  </span>
                )}
              </div>
            </div>

            <div className="flex bg-stone-100 rounded-full p-0.5 flex-shrink-0">
              <button onClick={() => handlePeriodChange('weekly')} className={`px-2.5 sm:px-3.5 py-1 text-xs sm:text-sm font-medium rounded-full transition ${period === 'weekly' ? 'bg-white text-stone-800 shadow-sm' : 'text-stone-400'}`} data-testid="shared-period-weekly">
                Weekly
              </button>
              <button onClick={() => handlePeriodChange('monthly')} className={`px-2.5 sm:px-3.5 py-1 text-xs sm:text-sm font-medium rounded-full transition ${period === 'monthly' ? 'bg-white text-stone-800 shadow-sm' : 'text-stone-400'}`} data-testid="shared-period-monthly">
                Monthly
              </button>
              <button onClick={() => handlePeriodChange('pending')} className={`px-2.5 sm:px-3.5 py-1 text-xs sm:text-sm font-medium rounded-full transition ${period === 'pending' ? 'bg-white text-red-600 shadow-sm' : 'text-stone-400'}`} data-testid="shared-period-pending">
                Pending
              </button>
            </div>
          </div>
          {isMemberView && !isPendingView && (
            <div className="mt-3 px-3 py-2.5 bg-white rounded-xl border" style={{ borderColor: `${viewer.color}55`, background: `linear-gradient(135deg, ${viewer.color}0a, transparent)` }} data-testid="viewer-total-card">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <span className="text-xs sm:text-sm text-stone-500">
                  {data?.period_payment?.paid ? 'Your total for this period (paid)' : 'Your total for this period'}
                </span>
                <div className="flex items-center gap-2">
                  {data?.period_payment?.paid && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200" data-testid="period-paid-badge">
                      ✓ Paid
                    </span>
                  )}
                  <span className={`text-lg sm:text-xl font-heading font-extrabold tabular-nums ${data?.period_payment?.paid ? 'text-emerald-700 line-through' : 'text-stone-900'}`}>
                    ₹{fmt(data?.viewer_total)}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-2 sm:px-6 py-4 sm:py-6">
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
          {isPendingView ? (
            loading ? (
              <div className="bg-white rounded-2xl shadow-[0_2px_8px_rgba(0,0,0,0.05)] p-12 text-center">
                <div className="animate-spin rounded-full h-6 w-6 border-2 border-[#4F6F52] border-t-transparent mx-auto"></div>
              </div>
            ) : (
              <PendingOrdersList
                data={pendingData}
                viewer={pendingData?.viewer}
                canMark={false}
                splittingEnabled={pendingData?.bill_splitting_enabled}
                compact
              />
            )
          ) : (
          <>
          {data?.bill_splitting_enabled && data?.member_totals?.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 mb-3 sm:mb-4" data-testid="shared-member-summary">
              {data.member_totals.map(m => (
                <div key={m.member_id} className="rounded-xl sm:rounded-2xl p-2.5 sm:p-3.5"
                  style={{ background: `linear-gradient(135deg, ${m.color}18, ${m.color}08)`, borderLeft: `3px solid ${m.color}` }}>
                  <div className="flex items-center gap-1.5 mb-1">
                    <div className="w-5 h-5 rounded-full flex items-center justify-center text-white font-bold text-[9px]" style={{ background: m.color }}>{m.name[0]?.toUpperCase()}</div>
                    <span className="text-[10px] sm:text-xs font-semibold text-stone-700 truncate">{m.name}</span>
                  </div>
                  <div className="text-sm sm:text-base font-heading font-extrabold text-stone-900 tabular-nums">₹{fmt(m.amount)}</div>
                </div>
              ))}
            </div>
          )}
          <div className="bg-white rounded-xl sm:rounded-2xl shadow-[0_2px_8px_rgba(0,0,0,0.05)]">
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-6 w-6 border-2 border-[#4F6F52] border-t-transparent mx-auto"></div>
              </div>
            ) : !data?.orders?.length ? (
              <div className="text-center py-12 px-4">
                <Calendar className="w-10 h-10 text-stone-200 mx-auto mb-2" />
                <p className="text-stone-500 text-sm font-medium">No orders for this period</p>
              </div>
            ) : (
              <>
                {/* Desktop table */}
                <div className="hidden sm:block" data-testid="shared-orders-table">
                  <table className="w-full table-fixed">
                    <thead>
                      <tr className="border-b border-stone-100">
                        <th className="w-[72px] px-5 py-3 text-left text-[10px] font-bold text-stone-400 uppercase tracking-widest">Date</th>
                        <th className="px-5 py-3 text-left text-[10px] font-bold text-stone-400 uppercase tracking-widest">Item</th>
                        <th className="w-[100px] px-5 py-3 text-right text-[10px] font-bold text-stone-400 uppercase tracking-widest">{isMemberView ? 'Bill' : 'Amount'}</th>
                        {!isMemberView && <th className="w-[100px] px-5 py-3 text-right text-[10px] font-bold text-stone-400 uppercase tracking-widest">Deducted</th>}
                        <th className="w-[110px] px-5 py-3 text-right text-[10px] font-bold text-stone-400 uppercase tracking-widest">{isMemberView ? 'You owe' : 'Final'}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.orders.map((order, idx) => (
                        <tr key={idx} className={`border-b border-stone-50 hover:bg-stone-50/60 transition ${idx % 2 !== 0 ? 'bg-stone-50/30' : ''}`}>
                          <td className="px-5 py-3.5 text-xs text-stone-600 font-medium whitespace-nowrap align-top">{formatDate(order.date)}</td>
                          <td className="px-5 py-3.5 align-top">
                            <div className="flex items-center gap-1">
                              <p className="text-xs font-semibold text-stone-800">{order.vendor_name || order.category || '-'}</p>
                              {order.receipt_link && (
                                <a href={order.receipt_link} target="_blank" rel="noopener noreferrer" className="text-[#4F6F52] hover:text-[#3f5942] flex-shrink-0">
                                  <ExternalLink className="w-3 h-3" />
                                </a>
                              )}
                            </div>
                            <DescriptionCell description={order.description} className="text-[11px]" />
                            {order.split_note && (
                              <p className="text-[10px] text-stone-500 mt-1 italic leading-snug" data-testid={`split-note-${idx}`}>{order.split_note}</p>
                            )}
                          </td>
                          <td className={`px-5 py-3.5 text-xs text-right tabular-nums align-top ${isMemberView ? 'text-stone-400' : 'text-stone-600'}`}>₹{fmt(order.amount)}</td>
                          {!isMemberView && (
                            <td className="px-5 py-3.5 text-xs text-right tabular-nums align-top">
                              <span className={`inline-flex items-center gap-0.5 ${order.deduction > 0 ? 'text-red-500 font-semibold' : 'text-stone-300'}`}>
                                {order.deduction > 0 ? `₹${fmt(order.deduction)}` : '-'}
                                {order.notes && (
                                  <Popover>
                                    <PopoverTrigger asChild>
                                      <button className={`p-0.5 rounded-full transition ${order.deduction > 0 ? 'hover:bg-red-100 text-red-400 hover:text-red-600' : 'hover:bg-blue-100 text-blue-400 hover:text-blue-600'}`}><Info className="w-3 h-3" /></button>
                                    </PopoverTrigger>
                                    <PopoverContent side="top" className="w-auto max-w-[220px] p-2.5 rounded-xl text-xs text-stone-600">{order.notes}</PopoverContent>
                                  </Popover>
                                )}
                              </span>
                            </td>
                          )}
                          <td className="px-5 py-3.5 text-xs text-stone-900 text-right font-bold tabular-nums align-top">
                            <span className="inline-flex items-center gap-1 justify-end">
                              ₹{fmt(isMemberView ? order.viewer_amount : order.final_amount)}
                              {isMemberView && order.split_note && (
                                <Popover>
                                  <PopoverTrigger asChild>
                                    <button className="p-0.5 rounded-full hover:bg-stone-100 text-stone-400 hover:text-stone-600 transition" data-testid={`split-info-${idx}`}>
                                      <Info className="w-3 h-3" />
                                    </button>
                                  </PopoverTrigger>
                                  <PopoverContent side="top" className="w-auto max-w-[240px] p-2.5 rounded-xl text-[11px] text-stone-600 italic leading-snug">
                                    {order.split_note}
                                  </PopoverContent>
                                </Popover>
                              )}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="border-t-2 border-stone-200 bg-stone-50/60">
                        <td className="px-5 py-3.5"></td>
                        <td className="px-5 py-3.5 text-xs font-bold text-stone-600">Total</td>
                        <td className={`px-5 py-3.5 text-xs text-right font-bold tabular-nums ${isMemberView ? 'text-stone-400' : 'text-stone-700'}`}>₹{fmt(data.total_amount)}</td>
                        {!isMemberView && (
                          <td className="px-5 py-3.5 text-xs text-right font-bold tabular-nums text-red-500">
                            {data.total_deductions > 0 ? `₹${fmt(data.total_deductions)}` : '-'}
                          </td>
                        )}
                        <td className="px-5 py-3.5 text-xs text-stone-900 text-right font-extrabold tabular-nums">₹{fmt(isMemberView ? data.viewer_total : data.total_final)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>

                {/* Mobile card list */}
                <div className="sm:hidden" data-testid="shared-orders-mobile">
                  {/* Mobile header */}
                  <div className="flex items-center px-3 py-2.5 border-b border-stone-100 text-[9px] font-bold text-stone-400 uppercase tracking-widest">
                    <span className="w-[52px] flex-shrink-0">Date</span>
                    <span className="flex-1 min-w-0">Item</span>
                    <span className="w-[58px] text-right flex-shrink-0">{isMemberView ? 'Bill' : 'Amt'}</span>
                    {!isMemberView && <span className="w-[52px] text-right flex-shrink-0">Ded.</span>}
                    <span className="w-[70px] text-right flex-shrink-0">{isMemberView ? 'You' : 'Final'}</span>
                  </div>

                  {data.orders.map((order, idx) => (
                    <div
                      key={idx}
                      className={`flex items-start px-3 py-2.5 border-b border-stone-50 ${idx % 2 !== 0 ? 'bg-stone-50/40' : ''}`}
                    >
                      <span className="w-[52px] flex-shrink-0 text-[10px] text-stone-500 font-medium pt-0.5">{formatDate(order.date)}</span>

                      <div className="flex-1 min-w-0 pr-1">
                        <div className="flex items-center gap-1">
                          <p className="text-[11px] font-semibold text-stone-800 truncate">{order.vendor_name || order.category || '-'}</p>
                          {order.receipt_link && (
                            <a href={order.receipt_link} target="_blank" rel="noopener noreferrer" className="text-[#4F6F52] flex-shrink-0">
                              <ExternalLink className="w-2.5 h-2.5" />
                            </a>
                          )}
                        </div>
                        <DescriptionCell description={order.description} className="text-[9px] leading-tight" mobileMaxLen={28} />
                        {order.split_note && (
                          <p className="text-[9px] text-stone-500 mt-0.5 italic leading-snug">{order.split_note}</p>
                        )}
                      </div>

                      <span className={`w-[58px] text-[10px] text-right tabular-nums flex-shrink-0 pt-0.5 ${isMemberView ? 'text-stone-400' : 'text-stone-600'}`}>₹{fmt(order.amount)}</span>

                      {!isMemberView && (
                        <span className={`w-[52px] text-[10px] text-right tabular-nums flex-shrink-0 pt-0.5 inline-flex items-center justify-end gap-0.5 ${order.deduction > 0 ? 'text-red-500 font-semibold' : 'text-stone-300'}`}>
                          {order.deduction > 0 ? `₹${fmt(order.deduction)}` : '-'}
                          {order.notes && (
                            <Popover>
                              <PopoverTrigger asChild>
                                <button className={`p-0.5 rounded-full ${order.deduction > 0 ? 'text-red-400' : 'text-blue-400'}`}><Info className="w-2.5 h-2.5" /></button>
                              </PopoverTrigger>
                              <PopoverContent side="top" className="w-auto max-w-[180px] p-2 rounded-xl text-[10px] text-stone-600">{order.notes}</PopoverContent>
                            </Popover>
                          )}
                        </span>
                      )}

                      <span className="w-[70px] text-[10px] text-stone-900 text-right font-bold tabular-nums flex-shrink-0 pt-0.5">
                        <span className="inline-flex items-center gap-0.5 justify-end">
                          ₹{fmt(isMemberView ? order.viewer_amount : order.final_amount)}
                          {isMemberView && order.split_note && (
                            <Popover>
                              <PopoverTrigger asChild>
                                <button className="p-0.5 rounded-full text-stone-400" data-testid={`split-info-mobile-${idx}`}>
                                  <Info className="w-2.5 h-2.5" />
                                </button>
                              </PopoverTrigger>
                              <PopoverContent side="top" className="w-auto max-w-[200px] p-2 rounded-xl text-[10px] text-stone-600 italic leading-snug">
                                {order.split_note}
                              </PopoverContent>
                            </Popover>
                          )}
                        </span>
                      </span>
                    </div>
                  ))}

                  {/* Mobile totals */}
                  <div className="flex items-center px-3 py-3 border-t-2 border-stone-200 bg-stone-50/60">
                    <span className="w-[52px] flex-shrink-0"></span>
                    <span className="flex-1 text-[10px] font-bold text-stone-600">Total</span>
                    <span className={`w-[58px] text-[10px] text-right font-bold tabular-nums flex-shrink-0 ${isMemberView ? 'text-stone-400' : 'text-stone-700'}`}>₹{fmt(data.total_amount)}</span>
                    {!isMemberView && (
                      <span className="w-[52px] text-[10px] text-right font-bold tabular-nums text-red-500 flex-shrink-0">
                        {data.total_deductions > 0 ? `₹${fmt(data.total_deductions)}` : '-'}
                      </span>
                    )}
                    <span className="w-[70px] text-[10px] text-stone-900 text-right font-extrabold tabular-nums flex-shrink-0">₹{fmt(isMemberView ? data.viewer_total : data.total_final)}</span>
                  </div>
                </div>
              </>
            )}
          </div>
          </>
          )}
        </motion.div>
      </main>
    </div>
  );
};

export default SharedDashboard;
